import { useCallback, useEffect, useRef, useState } from 'react';
import {
    buildWaveformLevels,
    createAudioFileFromBlob,
    getSupportedAudioRecordingConfig,
    requestMicrophoneStream,
    type TAudioRecorderStatus,
} from '../utils/audioRecorder';
import { transcodeAudioBlobToMp3 } from '../utils/normalizeAudioForMetaUpload';

const MIN_RECORDING_MS = 1000;

const supportsRecorderResume = (mediaRecorder: MediaRecorder) =>
    typeof mediaRecorder.resume === 'function';

export const useAudioRecorder = () => {
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const timerRef = useRef<number | null>(null);
    const waveformFrameRef = useRef<number | null>(null);
    const elapsedMsRef = useRef(0);
    const tickAnchorRef = useRef(0);
    const mimeTypeRef = useRef('');
    const previewUrlRef = useRef<string | null>(null);

    const [status, setStatus] = useState<TAudioRecorderStatus>('idle');
    const [durationMs, setDurationMs] = useState(0);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [canResumeRecording, setCanResumeRecording] = useState(false);
    const [waveformLevels, setWaveformLevels] = useState<number[]>(
        Array.from({ length: 28 }, () => 0.12)
    );

    const stopMediaTracks = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
    }, []);

    const stopWaveformLoop = useCallback(() => {
        if (waveformFrameRef.current) {
            cancelAnimationFrame(waveformFrameRef.current);
            waveformFrameRef.current = null;
        }
    }, []);

    const stopTimer = useCallback(() => {
        if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    const closeAudioContext = useCallback(() => {
        if (audioContextRef.current?.state !== 'closed') {
            void audioContextRef.current?.close();
        }
        audioContextRef.current = null;
        analyserRef.current = null;
    }, []);

    const revokePreviewUrl = useCallback(() => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewUrl(null);
    }, []);

    const releaseRecordingResources = useCallback(() => {
        stopMediaTracks();
        closeAudioContext();
        mediaRecorderRef.current = null;
        revokePreviewUrl();
    }, [closeAudioContext, revokePreviewUrl, stopMediaTracks]);

    const buildBlobFromChunks = useCallback(() => {
        if (!chunksRef.current.length || !mimeTypeRef.current) return null;

        return new Blob(chunksRef.current, {
            type: mimeTypeRef.current,
        });
    }, []);

    const buildFileFromChunks = useCallback(() => {
        const blob = buildBlobFromChunks();
        if (!blob) return null;

        return createAudioFileFromBlob(blob, mimeTypeRef.current);
    }, [buildBlobFromChunks]);

    const updatePreviewFromChunks = useCallback(async () => {
        revokePreviewUrl();

        const blob = buildBlobFromChunks();
        if (!blob || blob.size === 0) return null;

        const mp3File = await transcodeAudioBlobToMp3(blob);
        const url = URL.createObjectURL(mp3File);
        previewUrlRef.current = url;
        setPreviewUrl(url);

        return mp3File;
    }, [buildBlobFromChunks, revokePreviewUrl]);

    const waitForRecorderData = useCallback((mediaRecorder: MediaRecorder) => {
        return new Promise<void>((resolve) => {
            mediaRecorder.addEventListener('dataavailable', () => resolve(), {
                once: true,
            });
            mediaRecorder.requestData();
        });
    }, []);

    const startWaveformLoop = useCallback(() => {
        const tick = () => {
            if (analyserRef.current) {
                setWaveformLevels(buildWaveformLevels(analyserRef.current));
            }

            waveformFrameRef.current = requestAnimationFrame(tick);
        };

        stopWaveformLoop();
        waveformFrameRef.current = requestAnimationFrame(tick);
    }, [stopWaveformLoop]);

    const startTimer = useCallback(() => {
        stopTimer();
        tickAnchorRef.current = Date.now();

        timerRef.current = window.setInterval(() => {
            setDurationMs(
                elapsedMsRef.current + Date.now() - tickAnchorRef.current
            );
        }, 200);
    }, [stopTimer]);

    const setupAnalyser = useCallback((stream: MediaStream) => {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();

        analyser.fftSize = 256;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
    }, []);

    const startRecording = useCallback(async () => {
        const audioConfig = getSupportedAudioRecordingConfig();

        if (!audioConfig) {
            throw new Error('Errors.MicrophoneNotSupported');
        }

        const stream = await requestMicrophoneStream();
        const mediaRecorder = new MediaRecorder(stream, {
            mimeType: audioConfig.mimeType,
            audioBitsPerSecond: 64000,
        });

        mimeTypeRef.current = audioConfig.mimeType;
        mediaStreamRef.current = stream;
        mediaRecorderRef.current = mediaRecorder;
        chunksRef.current = [];
        elapsedMsRef.current = 0;

        mediaRecorder.ondataavailable = (event) => {
            if (event.data.size > 0) {
                chunksRef.current.push(event.data);
            }
        };

        setupAnalyser(stream);
        setCanResumeRecording(supportsRecorderResume(mediaRecorder));
        mediaRecorder.start(1000);
        setDurationMs(0);
        setStatus('recording');
        startTimer();
        startWaveformLoop();
    }, [setupAnalyser, startTimer, startWaveformLoop]);

    const pauseRecording = useCallback(() => {
        const mediaRecorder = mediaRecorderRef.current;

        if (!mediaRecorder || mediaRecorder.state !== 'recording') {
            return;
        }

        void (async () => {
            elapsedMsRef.current += Date.now() - tickAnchorRef.current;
            setDurationMs(elapsedMsRef.current);
            stopTimer();
            stopWaveformLoop();

            await waitForRecorderData(mediaRecorder);

            if (mediaRecorder.state === 'recording') {
                mediaRecorder.pause();
            }

            if (audioContextRef.current?.state === 'running') {
                await audioContextRef.current.suspend();
            }

            try {
                await updatePreviewFromChunks();
                setStatus('paused');
            } catch {
                if (audioContextRef.current?.state === 'suspended') {
                    await audioContextRef.current.resume();
                }
                mediaRecorder.resume();
                tickAnchorRef.current = Date.now();
                startTimer();
                startWaveformLoop();
            }
        })();
    }, [
        startTimer,
        startWaveformLoop,
        stopTimer,
        stopWaveformLoop,
        updatePreviewFromChunks,
        waitForRecorderData,
    ]);

    const resumeRecording = useCallback(() => {
        const mediaRecorder = mediaRecorderRef.current;

        if (
            !mediaRecorder ||
            mediaRecorder.state !== 'paused' ||
            !supportsRecorderResume(mediaRecorder)
        ) {
            return;
        }

        revokePreviewUrl();
        mediaRecorder.resume();

        if (audioContextRef.current?.state === 'suspended') {
            void audioContextRef.current.resume();
        }

        tickAnchorRef.current = Date.now();
        setStatus('recording');
        startTimer();
        startWaveformLoop();
    }, [revokePreviewUrl, startTimer, startWaveformLoop]);

    const stopAndGetFile = useCallback(async () => {
        const mediaRecorder = mediaRecorderRef.current;

        if (!mediaRecorder || mediaRecorder.state === 'inactive') {
            return null;
        }

        if (mediaRecorder.state === 'recording') {
            elapsedMsRef.current += Date.now() - tickAnchorRef.current;
        }

        stopTimer();
        stopWaveformLoop();

        await new Promise<void>((resolve) => {
            mediaRecorder.addEventListener('stop', () => resolve(), {
                once: true,
            });
            mediaRecorder.requestData();
            mediaRecorder.stop();
        });

        const file = buildFileFromChunks();

        releaseRecordingResources();
        chunksRef.current = [];
        mimeTypeRef.current = '';
        elapsedMsRef.current = 0;
        setDurationMs(0);
        setWaveformLevels(Array.from({ length: 28 }, () => 0.12));
        setCanResumeRecording(false);
        setStatus('idle');

        return file;
    }, [
        buildFileFromChunks,
        releaseRecordingResources,
        stopTimer,
        stopWaveformLoop,
    ]);

    const discardRecording = useCallback(() => {
        stopTimer();
        stopWaveformLoop();

        const mediaRecorder = mediaRecorderRef.current;

        if (mediaRecorder && mediaRecorder.state !== 'inactive') {
            mediaRecorder.onstop = null;
            mediaRecorder.stop();
        }

        releaseRecordingResources();
        chunksRef.current = [];
        mimeTypeRef.current = '';
        elapsedMsRef.current = 0;
        setDurationMs(0);
        setWaveformLevels(Array.from({ length: 28 }, () => 0.12));
        setCanResumeRecording(false);
        setStatus('idle');
    }, [releaseRecordingResources, stopTimer, stopWaveformLoop]);

    const canSendRecording =
        (status === 'recording' || status === 'paused') &&
        durationMs >= MIN_RECORDING_MS;

    useEffect(
        () => () => {
            discardRecording();
        },
        [discardRecording]
    );

    return {
        status,
        durationMs,
        previewUrl,
        waveformLevels,
        canResumeRecording,
        startRecording,
        pauseRecording,
        resumeRecording,
        stopAndGetFile,
        discardRecording,
        isActive: status === 'recording' || status === 'paused',
        canSendRecording,
    };
};
